import { useGLTF, useCursor, CameraControls, Html } from "@react-three/drei";
import React, { useEffect, useRef, useState } from "react";
import { cameraTransform, cameraStart } from "./data/CamAnimation";
import gsap from "gsap";
import UseGame from "./stores/UseGame";
import * as THREE from "three";
import cursor_image from "/images/cursor.png";

export default function Clickables({ center }) {
  const cameraVector = new THREE.Vector3(cameraStart);
  let centerY = -8;

  //Only have one animation going
  let animation = null;

  const { nodes } = useGLTF("/model/M_ISLAND.glb");

  const controls = useRef();

  const phase = UseGame((state) => state.phase);
  const story = UseGame((state) => state.story);
  const clickables = UseGame((state) => state.clickables);
  const setIndex = UseGame((state) => state.setIndex);
  const index = UseGame((state) => state.index);
  const moving = UseGame((state) => state.moving);
  const setMoving = UseGame((state) => state.setMoving);
  const setObject = UseGame((state) => state.setObject);

  const animateCamera = () => {
    controls.current.enabled = false;
    if (animation) {
      animation.kill(); // Kill any ongoing animation
    }
    const pos = cameraTransform[clickables[index]];
    cameraVector.set(pos.vx, pos.vy, pos.vz);
    centerY = pos.cy;

    if (controls.current) {
      animation = gsap.to(controls.current.camera.position, {
        duration: 1.8,
        delay: 0.1,
        x: cameraVector.x,
        y: cameraVector.y,
        z: cameraVector.z,
        ease: "power1.out",
        onUpdate: () => {
          if (controls.current) {
            controls.current.camera.lookAt(
              cameraTransform[clickables[index]].lookAt
            );
            center.current.position.y = centerY;
          }
        },
        onComplete: () => {
          setMoving(false);
        },
      });
    }
  };

  const resetCamera = () => {
    //re-enable orbitcontrols
    controls.current.enabled = true;
    //reset look at position
    controls.current.camera.lookAt(new THREE.Vector3(0, 0, 0));
    //Reset camera position
    cameraVector.set(cameraStart);
    //Reset Center component's y position
    center.current.position.y = centerY;
  };

  useEffect(() => {
    if (moving) {
      story();
      animateCamera();
    }
  }, [moving]);

  useEffect(() => {
    //if phase is ready reset position
    if (phase == "ready") {
      resetCamera();
    }
  }, [phase]);

  const setClicked = (event, name) => {
    event.stopPropagation();
    setIndex(clickables.indexOf(name));
    setMoving(true);
    setObject(name);

    story();
  };

  return (
    <>
      <CameraControls
        makeDefault
        maxDistance={150}
        maxPolarAngle={Math.PI / 1.8}
        minPolarAngle={Math.PI / 4}
        enablePan={false}
        ref={controls}
      />
      <group>
        <TransparentBox
          position={nodes.M_STATUE_PERSON.position}
          scale={[4, 5.5, 3]}
          onClick={(e) => setClicked(e, "statue")}
          name="statue"
          navName="About me"
        />
        <TransparentBox
          position={new THREE.Vector3(0, 2, -14)}
          scale={[6, 8.1, 7.6]}
          onClick={(e) => setClicked(e, "tavern")}
          name="tavern"
          navName="Projects"
        />
        <TransparentBox
          position={new THREE.Vector3(0, 1, 0)}
          scale={[3, 3.5, 3]}
          onClick={(e) => setClicked(e, "well")}
          name="well"
          navName="My dream"
        />
        <TransparentBox
          position={nodes.M_STAGE.position}
          scale={[4, 4, 5.5]}
          onClick={(e) => setClicked(e, "stage")}
          name="stage"
          navName="Information"
        />
        <TransparentBox
          position={nodes.M_VIKING_BOAT.position}
          scale={[6, 6, 6]}
          onClick={(e) => setClicked(e, "boat")}
          name="boat"
          navName="Erasmus"
        />
        <TransparentBox
          position={nodes.M_GUY_READING_HEAD.position}
          scale={[2, 3, 2]}
          onClick={(e) => setClicked(e, "reader")}
          name="reader"
          navName="Research papers"
        />
        <TransparentBox
          position={nodes.M_GUITAR_UPPER.position}
          scale={[2, 2.5, 2]}
          onClick={(e) => setClicked(e, "musician")}
          name="musician"
          navName="music"
        />
        <TransparentBox
          position={nodes.M_FLAG_BELGIUM.position}
          scale={[4, 3, 3]}
          onClick={(e) => setClicked(e, "flag")}
          name="flag"
          navName="Origin"
        />
        <TransparentBox
          position={nodes.M_ROUTE_POLE.position}
          scale={[3.5, 4, 3]}
          onClick={(e) => setClicked(e, "route")}
          name="route"
          navName="Links"
        />
      </group>
    </>
  );
}

function TransparentBox({ position, scale, name, navName, ...props }) {
  const [hovered, setHovered] = useState("");
  useCursor(hovered, "none", "auto", document.body);
  const help = UseGame((state) => state.help);
  const phase = UseGame((state) => state.phase);

  useEffect(() => {
    cameraTransform[name].lookAt = position;
  }, []);

  useEffect(() => {
    var element = document.querySelector(".custom-cursor");
    if (hovered) {
      element.className = "custom-cursor active";
    } else {
      element.className = "custom-cursor";
    }
  }, [hovered]);

  return (
    <>
      <mesh
        position={position}
        scale={scale}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        {...props}
        dispose={null}
      >
        <boxGeometry />
        <meshPhongMaterial color="#ff0000" opacity={0} transparent />
      </mesh>
      {help && phase === "ready" && (
        <Html
          position={position}
          position-x={position.x - 0.5}
          position-y={position.y + 0.5}
          position-z={position.z - 0.25}
          className="object-html"
        >
          <h1 className="object-text">{navName}</h1>
        </Html>
      )}
    </>
  );
}
